import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../../app/store-types';
import { orderDbId, orderIsPaid } from './lib/order';
import { cartQuantity, cartTotal, recalcLineDiscounts } from './lib/totals';
import { loadPosState } from './pos.persist';
import type { CartLine, Discount, Operator, PosSearchResult } from './pos.schema';

// The scanned customer attached to a bon — drives custom-pricelist repricing
// and stamps the placed order with this user id. Minimal by design; the full
// customer record (balance, loyalty) lives in the CustomerDialog query.
export interface PosCustomerRef {
    id: number;
    name: string | null;
}

// One open bon — its own cart, discounts, customer, and loaded-order/reservation
// context. Multiple tabs let a cashier juggle several sales at once (e.g. one per
// table).
export interface PosTab {
    id: string;
    label: string;
    initialized: boolean;
    saleType: string | null;
    products: CartLine[];
    discounts: number[]; // global value discounts
    customer: PosCustomerRef | null;
    orderNumber: string | null;
    orderType: string | null;
    orderData: Record<string, unknown>;
    reservationCode: string | null;
}

// The operator (cashier) is shared across all open bonuri; the tabs are the bonuri.
export interface PosState {
    operator: Operator | null;
    tabs: PosTab[];
    activeTabId: string;
    tabCounter: number; // monotonic — drives both tab ids and default "Nota N" labels
}

function makeTab(id: string, label: string): PosTab {
    return {
        id,
        label,
        initialized: false,
        saleType: null,
        products: [],
        discounts: [],
        customer: null,
        orderNumber: null,
        orderType: null,
        orderData: {},
        reservationCode: null,
    };
}

// The first tab is the fixed "main" bon: always this label, never renameable.
const MAIN_TAB_LABEL = 'Vanzare';

// Cap on concurrent open bonuri (the main tab plus extras).
export const MAX_TABS = 5;

function defaultState(): PosState {
    return {
        operator: null,
        tabs: [makeTab('tab-1', MAIN_TAB_LABEL)],
        activeTabId: 'tab-1',
        tabCounter: 1,
    };
}

const initialState: PosState = (() => {
    const s = loadPosState() ?? defaultState();
    // Normalize the main tab's label so terminals with persisted state (e.g. an
    // old "Bon 1") pick up the fixed name without losing their open bonuri.
    if (s.tabs[0]) s.tabs[0].label = MAIN_TAB_LABEL;
    // Orders now live in their own tabs; a session persisted before that rule
    // may still hold one on the main bon — move it out instead of dropping it.
    const main = s.tabs[0];
    if (main?.orderNumber) {
        s.tabCounter += 1;
        const moved: PosTab = { ...main, id: `tab-${s.tabCounter}`, label: `#${main.orderNumber}` };
        Object.assign(main, makeTab(main.id, MAIN_TAB_LABEL));
        s.tabs.push(moved);
        if (s.activeTabId === main.id) s.activeTabId = moved.id;
    }
    return s;
})();

// Active tab. Tabs are guaranteed non-empty (closing the last one spawns a fresh
// tab), so the fallback only guards against a malformed persisted state.
function activeTab(state: PosState): PosTab {
    return state.tabs.find((t) => t.id === state.activeTabId) ?? state.tabs[0];
}

// Empty a bon's sale data, keeping the tab itself (its id/label).
function clearBon(t: PosTab) {
    t.products = [];
    t.discounts = [];
    t.customer = null;
    t.orderNumber = null;
    t.orderType = null;
    t.orderData = {};
    t.reservationCode = null;
}

// Marker on the auto-applied custom-pricelist discount, so repricing can strip
// the previous one without touching the cashier's manual discounts.
const CUSTOMER_DISCOUNT_SOURCE = 'customer';

const round2 = (n: number) => Math.round(n * 100) / 100;

function toLine(result: PosSearchResult, price?: number, quantity = 1): CartLine {
    return {
        id: String(result.id),
        name: result.name,
        price: price ?? result.price,
        quantity,
        vatBracket: result.vatBracket,
        stock: result.quantity,
        kind: result.kind,
        thumbnail: result.thumbnail ?? null,
        game: result.game ?? null,
        discounts: [],
    };
}

const posSlice = createSlice({
    name: 'pos',
    initialState,
    reducers: {
        setOperator(state, action: PayloadAction<Operator | null>) {
            state.operator = action.payload;
            // Logout clears every open bon back to a single fresh tab.
            if (!action.payload) {
                state.tabs = [makeTab('tab-1', MAIN_TAB_LABEL)];
                state.activeTabId = 'tab-1';
                state.tabCounter = 1;
            }
        },

        // --- tab management ---
        addTab(state) {
            if (state.tabs.length >= MAX_TABS) return; // cap concurrent bonuri
            state.tabCounter += 1;
            const id = `tab-${state.tabCounter}`;
            state.tabs.push(makeTab(id, `Nota ${state.tabCounter}`));
            state.activeTabId = id;
        },
        setActiveTab(state, action: PayloadAction<string>) {
            if (state.tabs.some((t) => t.id === action.payload)) {
                state.activeTabId = action.payload;
            }
        },
        closeTab(state, action: PayloadAction<string>) {
            const idx = state.tabs.findIndex((t) => t.id === action.payload);
            // The first tab is the un-closable "main" bon. (Charging resets it
            // rather than closing — see paymentReceived.)
            if (idx <= 0) return;
            state.tabs.splice(idx, 1);
            if (state.activeTabId === action.payload) {
                state.activeTabId = state.tabs[Math.min(idx, state.tabs.length - 1)].id;
            }
        },
        renameTab(state, action: PayloadAction<{ id: string; label: string }>) {
            const idx = state.tabs.findIndex((t) => t.id === action.payload.id);
            if (idx <= 0) return; // the main bon's name is fixed
            state.tabs[idx].label = action.payload.label.trim() || state.tabs[idx].label;
        },

        // --- active-bon operations ---
        initSale(state, action: PayloadAction<string>) {
            const t = activeTab(state);
            t.initialized = true;
            t.saleType = action.payload;
        },
        endSale(state) {
            const t = activeTab(state);
            const { id, label } = t;
            Object.assign(t, makeTab(id, label));
        },
        addLine(
            state,
            action: PayloadAction<{ result: PosSearchResult; price?: number; quantity?: number }>,
        ) {
            const { result, price, quantity } = action.payload;
            const t = activeTab(state);
            t.initialized = true;
            const existing = t.products.find((p) => p.id === String(result.id));
            if (existing) {
                existing.quantity += quantity ?? 1;
            } else {
                t.products.push(toLine(result, price, quantity ?? 1));
            }
        },
        changeQuantity(state, action: PayloadAction<{ id: string; quantity: number }>) {
            const t = activeTab(state);
            const line = t.products.find((p) => p.id === action.payload.id);
            if (line) {
                line.quantity = action.payload.quantity;
                recalcLineDiscounts(line);
            }
            t.products = t.products.filter((p) => p.quantity > 0);
        },
        applyLineDiscount(
            state,
            action: PayloadAction<{ id: string; type: Discount['type']; discount: number }>,
        ) {
            const line = activeTab(state).products.find((p) => p.id === action.payload.id);
            if (line) {
                line.discounts.push({
                    type: action.payload.type,
                    discount: action.payload.discount,
                    value: null,
                });
                recalcLineDiscounts(line);
            }
        },
        removeLineDiscount(state, action: PayloadAction<{ id: string; discount: Discount }>) {
            const line = activeTab(state).products.find((p) => p.id === action.payload.id);
            if (line) {
                line.discounts = line.discounts.filter(
                    (d) =>
                        d.type !== action.payload.discount.type ||
                        d.value !== action.payload.discount.value,
                );
                recalcLineDiscounts(line);
            }
        },
        addGlobalDiscount(state, action: PayloadAction<number>) {
            activeTab(state).discounts.push(action.payload);
        },
        removeGlobalDiscount(state, action: PayloadAction<number>) {
            const t = activeTab(state);
            const idx = t.discounts.indexOf(action.payload);
            if (idx !== -1) t.discounts.splice(idx, 1);
        },

        // --- customer (loyalty card) on the bon ---
        // Attach a scanned customer to the active bon. Detaching (null) also
        // strips every auto-applied custom-price discount so the bon falls back
        // to catalog prices; the reprice effect re-applies on the next attach.
        setCustomer(state, action: PayloadAction<PosCustomerRef | null>) {
            const t = activeTab(state);
            t.customer = action.payload;
            if (!action.payload) {
                for (const line of t.products) {
                    line.discounts = line.discounts.filter(
                        (d) => d.source !== CUSTOMER_DISCOUNT_SOURCE,
                    );
                    recalcLineDiscounts(line);
                }
            }
        },
        // Re-apply the resolved custom-pricelist prices as a labelled per-line
        // discount (base price − customer price). Only reductions are expressed
        // (the discount-line representation can't carry an up-charge); a missing
        // or higher resolved price leaves the line at its catalog price.
        applyCustomerPricing(
            state,
            action: PayloadAction<{ prices: { id: string; price: number | null }[] }>,
        ) {
            const byId = new Map(action.payload.prices.map((p) => [p.id, p.price]));
            for (const line of activeTab(state).products) {
                line.discounts = line.discounts.filter(
                    (d) => d.source !== CUSTOMER_DISCOUNT_SOURCE,
                );
                const resolved = byId.get(line.id);
                if (resolved != null && resolved < line.price) {
                    line.discounts.unshift({
                        type: 'value',
                        discount: round2(line.price - resolved),
                        value: null,
                        source: CUSTOMER_DISCOUNT_SOURCE,
                    });
                }
                recalcLineDiscounts(line);
            }
        },
        clearReceipt(state) {
            clearBon(activeTab(state));
        },
        paymentReceived(state) {
            const idx = state.tabs.findIndex((t) => t.id === state.activeTabId);
            // After charging, close the bon — except the main (first) tab, which is
            // never closed, just reset for the next sale.
            if (idx <= 0) {
                clearBon(state.tabs[idx >= 0 ? idx : 0]);
                return;
            }
            state.tabs.splice(idx, 1);
            state.activeTabId = state.tabs[Math.min(idx, state.tabs.length - 1)].id;
        },
        loadFromOrder(
            state,
            action: PayloadAction<{
                orderNumber: string;
                orderType?: string | null;
                orderData?: Record<string, unknown>;
                lines: CartLine[];
            }>,
        ) {
            // A loaded order always occupies its own tab — never the main bon.
            // Re-loading an already-open order refreshes that tab instead of
            // duplicating it. Callers guard MAX_TABS; the no-op is a backstop.
            let t = state.tabs.find((tab) => tab.orderNumber === action.payload.orderNumber);
            if (!t) {
                if (state.tabs.length >= MAX_TABS) return;
                state.tabCounter += 1;
                t = makeTab(`tab-${state.tabCounter}`, `#${action.payload.orderNumber}`);
                state.tabs.push(t);
            }
            state.activeTabId = t.id;
            t.initialized = true;
            t.orderNumber = action.payload.orderNumber;
            t.orderType = action.payload.orderType ?? null;
            t.orderData = action.payload.orderData ?? {};
            t.products = action.payload.lines;
            t.discounts = [];
            t.reservationCode = null;
        },
        loadReservation(state, action: PayloadAction<{ reservationCode: string; line: CartLine }>) {
            const t = activeTab(state);
            t.initialized = true;
            t.reservationCode = action.payload.reservationCode;
            t.products.push(action.payload.line);
        },
    },
});

export const posActions = posSlice.actions;
export const posReducer = posSlice.reducer;

// RootState is loosely typed (configureStore.js is still JS), so reach into the
// slice through a single cast helper rather than scattering `as any`.
export const selectPos = (state: RootState) => (state as unknown as { pos: PosState }).pos;

const selectActiveTab = (state: RootState): PosTab | undefined => {
    const pos = selectPos(state);
    return pos.tabs.find((t) => t.id === pos.activeTabId) ?? pos.tabs[0];
};

export const selectOperator = (state: RootState) => selectPos(state).operator;
export const selectTabs = (state: RootState) => selectPos(state).tabs;
export const selectActiveTabId = (state: RootState) => selectPos(state).activeTabId;
export const selectCart = (state: RootState) => selectActiveTab(state)?.products ?? [];
export const selectGlobalDiscounts = (state: RootState) => selectActiveTab(state)?.discounts ?? [];
export const selectCustomer = (state: RootState) => selectActiveTab(state)?.customer ?? null;
export const selectInitialized = (state: RootState) => selectActiveTab(state)?.initialized ?? false;
export const selectOrderNumber = (state: RootState) => selectActiveTab(state)?.orderNumber ?? null;
export const selectOrderPaid = (state: RootState) => orderIsPaid(selectActiveTab(state)?.orderData);
export const selectOrderId = (state: RootState) => orderDbId(selectActiveTab(state)?.orderData);
export const selectReservationCode = (state: RootState) =>
    selectActiveTab(state)?.reservationCode ?? null;
export const selectTotal = (state: RootState) => {
    const t = selectActiveTab(state);
    return t ? cartTotal(t.products, t.discounts) : 0;
};
export const selectQuantity = (state: RootState) => {
    const t = selectActiveTab(state);
    return t ? cartQuantity(t.products) : 0;
};
